import React, { FC } from "react";
import { Button, Checkbox, Heading, Icon, Link, Text, Select } from "@telia/teddy";
import _get from "lodash/get";
import _isEmpty from "lodash/isEmpty";
import { useSelector } from "react-redux";
import { Discount, RootState } from "../../../../../../models";
import { BusinessProduct, OfferingB2B } from "../../../../../../models/state";
import { FieldSet, FormRowFormats, FormRow, Input } from "../../../../forms";
import { VALIDATION_FIELD } from "../../../../rapidform/formValidation";
import { isMbb as isMbbSelector } from "../../../state/selectors/businessProductsSelector";
import TEXT from "../../../text-consts-b2b";
import "./B2BProductCard.less";
import { Container } from "../../../../components";

interface B2BProductProps {
    product: Partial<BusinessProduct>;
    toggleInsurance: (e?: React.ChangeEvent<HTMLInputElement>) => void;
    wantInsurance: boolean;
}

const B2BProduct: FC<B2BProductProps> = ({ product, toggleInsurance, wantInsurance }) => {
    const { name } = product;
    const isMbb = useSelector((state: RootState) => isMbbSelector(state));
    const salesPriceWithoutVat = _get(product, "salesPriceWithoutVat", -1);

    return (
        <div className="b2b-product-card__form b2b-product-card__hardware-container">
            <div>
                <Heading mb="100" as="h2" variant="title-200" className="b2b-product-card__hardware-heading">
                    {TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.FORM.HEADING_HW(isMbb) + name}
                </Heading>
                {!isMbb && (
                    <div className="b2b-product-card__insurance-checkbox">
                        <Checkbox>
                            <Checkbox.Trigger checked={wantInsurance} onCheckedChange={() => toggleInsurance()} />
                            <span>{TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.FORM.INSURANCE}</span>
                        </Checkbox>
                    </div>
                )}
                <Text mb="100">
                    {isMbb && salesPriceWithoutVat > -1
                        ? TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.FORM.ROUTER_PRICE(Math.round(salesPriceWithoutVat))
                        : TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.FORM.PHONE_PRICE(Math.round(salesPriceWithoutVat))}
                </Text>
            </div>
            <div className="b2b-product-card__hardware-edit-btn">
                <Link href={isMbb ? "/bedrift/mobilt-bredband/" : "/bedrift/mobiltelefoner"} variant="text">
                    <Icon name="switch-arrows" aria-hidden="true" focusable="false" />
                    {TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.EDIT_HW(isMbb)}
                </Link>
            </div>
        </div>
    );
};

interface B2BProductCardProps {
    addProduct: () => void;
    deleteProduct: (index: number, subscription: OfferingB2B) => void;
    formatName: (a: string, b: string) => string;
    ids: number[];
    isNewNumbers: boolean[];
    list: number[];
    offeringsForCart: OfferingB2B[];
    product: Partial<BusinessProduct>;
    selectedSubscriptions: OfferingB2B[];
    toggleInsurance: (e?: React.ChangeEvent<HTMLInputElement>) => void;
    updateIsNewNumber: (isNewNumber: boolean, index: number) => void;
    updateSelectedSubscription: (subscription: OfferingB2B, index: number) => void;
    wantInsurance: boolean;
    webdealsInCart: Discount[];
}

const B2BProductCard: FC<B2BProductCardProps> = ({
    ids,
    updateIsNewNumber,
    product,
    formatName,
    toggleInsurance,
    wantInsurance,
    isNewNumbers,
    updateSelectedSubscription,
    selectedSubscriptions,
    offeringsForCart,
    webdealsInCart,
    addProduct,
    list,
    deleteProduct,
}) => (
    <Container className="b2b-order__section b2b-product-card" size="large" whiteBg>
        <Heading mb="100" as="h2" variant="title-300">
            {TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.HEADING}
        </Heading>
        {!_isEmpty(product) && (
            <B2BProduct wantInsurance={wantInsurance} toggleInsurance={toggleInsurance} product={product} />
        )}
        {!_isEmpty(webdealsInCart) &&
            webdealsInCart.map((deal) => {
                const { code, descriptionInCheckout } = deal;
                return (
                    <div key={`b2b-product-card-deal-${code}`} className="b2b-product-card__form">
                        <Heading mb="100" as="h2" variant="title-200" className="b2b-product-card__webdeal-heading">
                            {TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.FORM.WEBDEAL}
                        </Heading>
                        <div dangerouslySetInnerHTML={{ __html: descriptionInCheckout }} />
                    </div>
                );
            })}
        {list.map((index) => (
            <div key={`b2b-product-card-${ids[index]}`} className="b2b-product-card__form">
                <div className="b2b-product-card__heading">
                    <Heading mb="100" as="h2" variant="title-200">
                        {TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.FORM.HEADING_SUBSCRIPTION}
                    </Heading>
                    {selectedSubscriptions.length > 1 && (
                        <span onClick={(): void => deleteProduct(index, selectedSubscriptions[index])}>
                            <Icon name="remove" />
                        </span>
                    )}
                </div>
                <FieldSet name={`productUser_${ids[index]}`}>
                    <FormRow format={FormRowFormats.THIRTY_THIRTY_THIRTY}>
                        <Select
                            name={`productSubscription_${ids[index]}`}
                            options={offeringsForCart.map((subscription) => subscription.offeringCode)}
                            value={selectedSubscriptions[index]?.offeringCode || ""}
                            onValueChange={(value) => {
                                const subscription = offeringsForCart.find((s) => s.offeringCode === value);
                                if (subscription) {
                                    updateSelectedSubscription(subscription, index);
                                }
                                return value;
                            }}
                            className="b2b-product-card__select"
                        >
                            <Select.Trigger type="button" className="b2b-product-card__select-trigger">
                                <Select.Value
                                    placeholder={formatName(
                                        selectedSubscriptions[index]?.name,
                                        selectedSubscriptions[index]?.monthlyPrice + "",
                                    )}
                                />
                            </Select.Trigger>
                            <Select.Content>
                                <Select.Viewport>
                                    {!_isEmpty(offeringsForCart) &&
                                        offeringsForCart.map((subscription) => (
                                            <Select.Item
                                                key={subscription.offeringCode}
                                                value={subscription.offeringCode}
                                            >
                                                {formatName(subscription.name, subscription.monthlyPrice + "")}
                                            </Select.Item>
                                        ))}
                                </Select.Viewport>
                            </Select.Content>
                        </Select>
                        <div />
                        <div />
                    </FormRow>
                    <FormRow format={FormRowFormats.THIRTY_THIRTY_THIRTY} data-di-mask>
                        <Input
                            type="text"
                            name={VALIDATION_FIELD.PRODUCT_USER_FIRST_NAME}
                            label={TEXT.PERSONAL_DETAILS.FIRSTNAME}
                        />
                        <Input
                            type="text"
                            name={VALIDATION_FIELD.PRODUCT_USER_LAST_NAME}
                            label={TEXT.PERSONAL_DETAILS.LASTNAME}
                        />
                        <Input
                            type="tel"
                            name={VALIDATION_FIELD.PRODUCT_USER_BIRTHDATE}
                            label={TEXT.PERSONAL_DETAILS.BIRTHDATE}
                        />
                    </FormRow>
                    <FormRow format={FormRowFormats.THIRTY_THIRTY_THIRTY} data-di-mask>
                        {isNewNumbers[index] && (
                            <Input
                                disabled={isNewNumbers[index]}
                                type="tel"
                                name={VALIDATION_FIELD.PRODUCT_USER_PHONE_NON_REQUIRED}
                                label={TEXT.COMMON.MOBILENUMBER}
                            />
                        )}
                        {!isNewNumbers[index] && (
                            <Input
                                type="tel"
                                name={VALIDATION_FIELD.PRODUCT_USER_PHONE}
                                label={TEXT.COMMON.MOBILENUMBER}
                            />
                        )}
                        <div className="b2b-product-card__checkbox">
                            <Checkbox>
                                <Checkbox.Trigger
                                    checked={isNewNumbers[index]}
                                    onCheckedChange={(checked) => updateIsNewNumber(!!checked, index)}
                                />
                                <span>{TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.NEW_NUMBER}</span>
                            </Checkbox>
                        </div>
                        <div>&nbsp;</div>
                    </FormRow>
                </FieldSet>
            </div>
        ))}
        {_isEmpty(product) && (
            <Button className="b2b-product-card__add-product" variant="text" onClick={addProduct}>
                <Icon name="simcard" aria-hidden="true" focusable="false" />
                {TEXT.B2B_MANUAL_ORDER.PRODUCT_CARD.ADD_SUBSCRIPTION}
            </Button>
        )}
    </Container>
);

export default B2BProductCard;
